import React, { useEffect, useRef, useState } from 'react';
import { FaEye, FaReply, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styles from '../CSS/TicketsTable.module.css';

const TicketsTable = ({
  loading,
  filteredTickets,
  departamentos,
  adminsDepartamento,
  formatDate,
  onViewTicket,
  onRespondTicket,
  onCloseTicket,
  // Nuevas props para paginación
  itemsPerPage = 5,
  setItemsPerPage,
  showPagination = true
}) => {
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  
  // Referencias existentes
  const scrollableRef = useRef(null);
  const tableRef = useRef(null);

  // Cálculos de paginación
  const totalItems = filteredTickets.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTickets = filteredTickets.slice(startIndex, endIndex);

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredTickets.length]);

  // Efecto existente para scroll
  useEffect(() => {
    const scrollableElement = scrollableRef.current;
    const tableElement = tableRef.current;
    
    if (!scrollableElement || !tableElement) return;
    
    const handleScroll = () => {
      if (scrollableElement.scrollLeft > 0) {
        scrollableElement.classList.add(styles.scrollActive);
      } else {
        scrollableElement.classList.remove(styles.scrollActive);
      }
    };
    
    const checkScrollNecessity = () => {
      handleScroll();
      
      if (tableElement.offsetWidth > scrollableElement.clientWidth) {
        scrollableElement.classList.add(styles.scrollNeeded);
      } else {
        scrollableElement.classList.remove(styles.scrollNeeded);
      }
    };
    
    scrollableElement.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkScrollNecessity);
    
    setTimeout(checkScrollNecessity, 100);
    
    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScrollNecessity);
    };
  }, []);

  // Funciones de navegación
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Generar números de página visibles
  const getVisiblePages = () => {
    const delta = 2; // Páginas a mostrar a cada lado de la actual
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  // Funciones auxiliares existentes
  const getDepartmentName = (departamentoId) => {
    if (!departamentos || !departamentos.length) return 'Sin departamento';
    const departamento = departamentos.find(d => d.id === departamentoId);
    return departamento ? departamento.nombre : 'Sin departamento';
  };

  // ✅ NUEVA FUNCIÓN: Obtener nombre del autor del ticket
  const getAuthorName = (ticket) => {
    // Prioridad: created_by_name > buscar en adminsDepartamento > email > fallback
    if (ticket.created_by_name) {
      return ticket.created_by_name;
    }
    
    // Buscar en la lista de admins de departamento
    if (adminsDepartamento && adminsDepartamento.length && ticket.created_by) {
      const autor = adminsDepartamento.find(admin => admin.id === ticket.created_by);
      if (autor) {
        return autor.nombre || autor.displayName || autor.email;
      }
    }
    
    // Si tiene email del creador, usarlo
    if (ticket.created_by_email) {
      return ticket.created_by_email;
    }
    
    // Fallback
    return 'Usuario desconocido';
  };

  // ✅ OPCIONAL: Mantener función para asignado (por si la necesitas en el futuro)
  const getAssignedName = (ticket) => {
    if (!ticket.assigned_to) return 'Sin asignar';
    
    if (!adminsDepartamento || !adminsDepartamento.length) {
      return ticket.assigned_to_name || 'Desconocido';
    }
    
    const admin = adminsDepartamento.find(a => a.id === ticket.assigned_to);
    return admin?.nombre || ticket.assigned_to_name || 'Desconocido';
  };

  const getStatusText = (estado) => {
    const statusMap = {
      'nuevo': 'NUEVO',
      'en_proceso': 'EN PROCESO',
      'escalado': 'ESCALADO',
      'cerrado': 'CERRADO'
    };
    return statusMap[estado] || estado;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}>
          <div className={styles.spinnerInner}></div>
        </div>
        <p>Cargando tickets...</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableOuterContainer} ref={scrollableRef}>
        <div className={styles.ticketsTable} ref={tableRef}>
          {/* Cabecera de la tabla */}
          <div className={styles.tableHeader}>
            <div className={styles.tableRow}>
              <div className={`${styles.headerCell} ${styles.idColumn}`}>ID</div>
              <div className={`${styles.headerCell} ${styles.titleColumn}`}>Título</div>
              <div className={`${styles.headerCell} ${styles.deptColumn}`}>Departamento</div>
              {/* ✅ CAMBIO: "Asignado" por "Autor" */}
              <div className={`${styles.headerCell} ${styles.assignedColumn}`}>Autor</div>
              <div className={`${styles.headerCell} ${styles.statusColumn}`}>Estado</div>
              <div className={`${styles.headerCell} ${styles.dateColumn}`}>Creado</div>
              <div className={`${styles.headerCell} ${styles.actionsColumn}`}>Acciones</div>
            </div>
          </div>
            
          {/* Cuerpo de la tabla */}
          <div className={styles.tableBody}>
            {currentTickets.length === 0 ? (
              <div className={styles.noTicketsMessage}>
                <div className={styles.noTicketsContent}>
                  <span className={styles.emptyStateIcon}>📋</span>
                  <span>
                    {totalItems === 0
                      ? 'No se encontraron tickets con los criterios seleccionados'
                      : 'No hay tickets en esta página'
                    }
                  </span>
                </div>
              </div>
            ) : (
              currentTickets.map(ticket => (
                <div
                  key={ticket.id}
                  className={styles.tableRow}
                  data-status={ticket.estado}
                  onClick={(e) => {
                    // Solo abrir si no se hizo clic en un botón
                    if (!e.target.closest('button')) {
                      onViewTicket(ticket);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.target.closest('button')) {
                      onViewTicket(ticket);
                    }
                  }}
                >
                  <div className={`${styles.tableCell} ${styles.idColumn}`}>
                    <span className={styles.idText}>{ticket.ticket_id || `#${ticket.id.slice(0, 6)}`}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.titleColumn}`} title={ticket.titulo}>
                    <span className={styles.titleText}>{ticket.titulo}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.deptColumn}`}>
                    <span className={styles.deptText}>{getDepartmentName(ticket.departamento_id)}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.assignedColumn}`}>
                    <span className={styles.assignedText}>{getAuthorName(ticket)}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.statusColumn}`}>
                    <span className={`${styles.statusBadge} ${styles[`status_${ticket.estado}`]}`}>
                      {getStatusText(ticket.estado)}
                    </span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.dateColumn}`}>
                    <span className={styles.dateText}>{formatDate(ticket.created_at)}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.actionsColumn}`}>
                    <div className={styles.actionButtons}>
                      <button
                        className={`${styles.actionButton} ${styles.viewButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewTicket(ticket);
                        }}
                        title="Ver detalles"
                        aria-label="Ver detalles del ticket"
                      >
                        <FaEye size={16} />
                      </button>

                      {ticket.estado !== 'cerrado' && (
                        <button
                          className={`${styles.actionButton} ${styles.replyButton}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRespondTicket(ticket);
                          }}
                          title="Responder"
                          aria-label="Responder al ticket"
                        >
                          <FaReply size={16} />
                        </button>
                      )}

                      {ticket.estado !== 'cerrado' && (
                        <button
                          className={`${styles.actionButton} ${styles.closeButton}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onCloseTicket(ticket);
                          }}
                          title="Cerrar ticket"
                          aria-label="Cerrar ticket"
                        >
                          <FaTimes size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Controles de paginación */}
      {showPagination && totalPages > 1 && (
        <div className={styles.paginationContainer}>
          {/* Info de página y selector - izquierda */}
          <div className={styles.paginationLeft}>
            <div className={styles.pageInfoSimple}>
              Página {currentPage} de {totalPages}
            </div>
          </div>

          {/* Controles centrales */}
          <div className={styles.paginationControls}>
            {/* Botón página anterior */}
            <button
              className={`${styles.paginationButton} ${styles.paginationNavButton}`}
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              title="Página anterior"
              aria-label="Ir a la página anterior"
            >
              <FaChevronLeft size={12} />
            </button>

            {/* Números de página */}
            <div className={styles.paginationNumbers}>
              {getVisiblePages().map((page, index) => (
                page === '...' ? (
                  <span key={`dots-${index}`} className={styles.paginationDots}>
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    className={`${styles.paginationButton} ${styles.paginationNumberButton} ${
                      currentPage === page ? styles.paginationActive : ''
                    }`}
                    onClick={() => goToPage(page)}
                    aria-label={`Ir a la página ${page}`}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </button>
                )
              ))}
            </div>

            {/* Botón página siguiente */}
            <button
              className={`${styles.paginationButton} ${styles.paginationNavButton}`}
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              title="Página siguiente"
              aria-label="Ir a la página siguiente"
            >
              <FaChevronRight size={12} />
            </button>
          </div>

          {/* Selector de items por página - derecha */}
          {setItemsPerPage && (
            <div className={styles.perPageSelector}>
              <label htmlFor="itemsPerPage">Mostrar:</label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className={styles.perPageSelect}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketsTable;